import { formatCurrencyValue } from '@eika/currency-input';

import type { Currency } from '../../../../../generated-types';
import { getCurrency } from '../../../../shared/numberFormat';

export const MIN_AMOUNT = 0;
export const MAX_AMOUNT = 999999999;

export const defineAndCompareAmountRange = (
    actualMinAmount: number | undefined,
    actualMaxAmount: number | undefined,
) => {
    const hasMinAmount = actualMinAmount !== undefined;
    const hasMaxAmount = actualMaxAmount !== undefined;
    const hasOnlyMinAmount = !hasMaxAmount && hasMinAmount;
    const hasOnlyMaxAmount = !hasMinAmount && hasMaxAmount;

    if (hasOnlyMaxAmount) {
        const maxAmount = actualMaxAmount;
        const minAmount = undefined;

        return { maxAmount, minAmount };
    }

    if (hasOnlyMinAmount) {
        const maxAmount = undefined;
        const minAmount = actualMinAmount;

        return { maxAmount, minAmount };
    }

    const minAmount =
        hasMinAmount && hasMaxAmount && actualMaxAmount < actualMinAmount ? actualMaxAmount : actualMinAmount;
    const maxAmount =
        hasMaxAmount && hasMinAmount && actualMinAmount < actualMaxAmount ? actualMaxAmount : actualMinAmount;

    return { maxAmount, minAmount };
};

export const formatAmountRange = (
    actualMinAmount: number | undefined,
    actualMaxAmount: number | undefined,
    prefix = '',
    currency: Currency = 'NOK',
) => {
    if ((actualMinAmount && actualMinAmount < MIN_AMOUNT) || (actualMaxAmount && actualMaxAmount < MIN_AMOUNT)) {
        return '';
    }
    const { maxAmount, minAmount } = defineAndCompareAmountRange(actualMinAmount, actualMaxAmount);
    const hasMinAmount = minAmount !== undefined;
    const hasMaxAmount = maxAmount !== undefined;

    if (hasMinAmount && (!hasMaxAmount || maxAmount === MAX_AMOUNT)) {
        const formattedPrefix = prefix !== '' ? `${prefix[0].toUpperCase() + prefix.slice(1)}, ` : '';
        return `${formattedPrefix}fra ${formatCurrencyValue(minAmount)}\xa0${getCurrency(currency ?? 'NOK')} og over`;
    }
    if (hasMaxAmount && (!hasMinAmount || minAmount === MIN_AMOUNT)) {
        const formattedPrefix = prefix !== '' ? `${prefix[0].toUpperCase() + prefix.slice(1)}, ` : '';
        return `${formattedPrefix}til ${formatCurrencyValue(maxAmount)}\xa0${getCurrency(currency ?? 'NOK')}`;
    }
    const currencyText = `\xa0${getCurrency(currency ?? 'NOK')}`;
    const formattedPrefix = prefix !== '' ? `${prefix} ` : '';
    return hasMinAmount && hasMaxAmount
        ? `${formattedPrefix}${formatCurrencyValue(minAmount)}${currencyText} til ${formatCurrencyValue(maxAmount)}${currencyText}`
        : undefined;
};
